import { zodResolver } from '@hookform/resolvers/zod';
import React, { useContext, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';

import { ApiContext } from '_lib/api/apiContext';
import { BadRequestError } from '_lib/api/apiErrors';
import {
  AccountResponseDTO,
  CallCenterDetailDTO,
  Company,
  companyCategoryDisplayName,
  CompanyCategoryEnum,
  CompanyCreateApiDTO,
  getCompanyCreateApiSchema,
  InvoicedServiceEnum
} from '_lib/api/apiTypes.shim';
import { WarningAlert } from '_lib/components/Alert/Alert';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { DialogActions } from '_lib/components/DialogActions/DialogActions';
import { DialogContent } from '_lib/components/DialogContent/DialogContent';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDetailsAccordion } from '_lib/components/ErrorDetailsAccordion/ErrorDetailsAccordion';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { Stack } from '_lib/components/Stack/Stack';
import { Step } from '_lib/components/Step/Step';
import { StepButton } from '_lib/components/StepButton/StepButton';
import { Stepper } from '_lib/components/Stepper/Stepper';
import { Typography } from '_lib/components/Typography/Typography';
import { AuthUser } from '_lib/featureComponents/auth/auth.types';
import { DialogWrapper } from '_lib/featureComponents/DialogWrapper/DialogWrapper';
import { TradesDisplay } from '_lib/featureComponents/Trades/TradesDisplay';
import { AsyncDialog, AsyncDialogProps, useAsyncDialog } from '_lib/hooks/useAsyncDialog';
import {
  extractAllErrors,
  extractUnmappableErrors,
  formatPhone,
  replaceEmptyStringsWithNull
} from '_lib/utils/helpers';
import { apiSubmitHandler } from '_lib/utils/hookFormUtils';
import { logDebug } from '_lib/utils/logging';
import { useOurSnackbar } from '_lib/utils/snackUtils';

import {
  CompanyConfigBillingForm,
  companyConfigBillingFormValues,
  CompanyConfigBillingFormValues
} from '../../CompanyFormItems/CompanyConfigBillingForm/CompanyConfigBillingForm';
import {
  CompanyConfigDetailForm,
  companyConfigDetailFormValues,
  CompanyConfigDetailFormValues
} from '../../CompanyFormItems/CompanyConfigDetailForm/CompanyConfigDetailForm';
import {
  CompanyConfigNotesForm,
  companyConfigNotesFormValues,
  CompanyConfigNotesFormValues
} from '../../CompanyFormItems/CompanyConfigNotesForm/CompanyConfigNotesForm';
import {
  CompanyConfigSettingsForm,
  companyConfigSettingsFormValues,
  CompanyConfigSettingsFormValues
} from '../../CompanyFormItems/CompanyConfigSettingsForm/CompanyConfigSettingsForm';
import {
  CompanyConfigZipZonesForm,
  companyConfigZipZonesFormValues,
  CompanyConfigZipZonesFormValues
} from '../../CompanyFormItems/CompanyConfigZipZonesForm/CompanyConfigZipZonesForm';
import {
  CompanyContactsForm,
  companyContactsFormValues,
  CompanyContactsFormValues
} from '../../CompanyFormItems/CompanyContactsForm/CompanyContactsForm';

type CompanyCreateDialogProps = AsyncDialogProps<{ createdCompany: Company }> & {
  account: AccountResponseDTO;
  callCenter: CallCenterDetailDTO;
  authUser: AuthUser;
};

const steps = [
  { label: 'Details', keys: companyConfigDetailFormValues },
  { label: 'Contacts', keys: companyContactsFormValues },
  { label: 'Notes', keys: companyConfigNotesFormValues },
  { label: 'Zip/Zones', keys: companyConfigZipZonesFormValues },
  { label: 'Settings', keys: companyConfigSettingsFormValues },
  { label: 'Billing', keys: companyConfigBillingFormValues },
  { label: 'Review', keys: [] }
];

export const CompanyCreateDialog = (props: CompanyCreateDialogProps) => {
  const { account, callCenter, authUser, onClose, ...dialogProps } = props;

  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<{
    [k: number]: boolean;
  }>({});
  const [validationErrors, setValidationErrors] = useState<string[]>();
  const [allErrors, setAllErrors] = useState<string[]>();

  const api = useContext(ApiContext);
  const { enqueueSnackbar } = useOurSnackbar();

  const appConfigQuery = api.appConfig.useAppConfigQuery();
  const aiVoicesQuery = api.appConfig.useAiVoiceConfigQuery();

  const companyCreateMutation = api.companies.useCreate();

  const companyCreateFormContext = useForm<CompanyCreateApiDTO>({
    context: { authUser },
    resolver: (values, context: { authUser: AuthUser }, options) => {
      const schema = getCompanyCreateApiSchema({
        callCenterId: authUser.callCenterId || callCenter.id,
        settings: authUser.settings
      });
      const resolver = zodResolver(schema);
      const normalizedValues = replaceEmptyStringsWithNull(values);
      return resolver(normalizedValues as typeof values, context, options);
    },
    mode: 'onChange',
    defaultValues: {
      accountId: account.id,
      callCenterId: authUser.callCenterId || callCenter.id,
      isManualEmployeeAssignEnabled: false,
      isScheduleEnabled: false,
      isZoned: false,
      zones: [],
      callCenter: {
        dnisArray: [],
        trades: [],
        reporting: {
          companyCategory: CompanyCategoryEnum.None,
          invoicedService: InvoicedServiceEnum.None,
          shouldReport: false
        },
        billing: {
          discount: 0,
          minimumWeeklyServiceFee: 315,
          perCall: 2.05,
          perAppointment: 29,
          perCancel: 0,
          weeklyIntegrationFee: 0
        },
        zipCodesServiced: [],
        phoneCallWebhookEnabled: false
      },
      emailSettings: {
        callPilot: {
          bccEmails: null,
          ccEmails: null,
          fromEmailAddress: null,
          plainTextEmails: null
        }
      }
    }
  });

  const {
    trigger,
    getValues,
    formState: { errors }
  } = companyCreateFormContext;

  const handleGoToStep = async (newStep: number) => {
    const isCurrentStepValid = await trigger(steps[activeStep] ? steps[activeStep].keys : undefined, {
      shouldFocus: true
    });
    if (isCurrentStepValid) {
      setActiveStep(newStep);
      setCompletedSteps((prev) => ({ ...prev, [activeStep]: true }));
      setValidationErrors(undefined);
      setAllErrors(undefined);
    } else {
      const unmappableErrors = extractUnmappableErrors(errors, steps[activeStep]?.keys || []);
      if (unmappableErrors.length > 0) {
        setValidationErrors(unmappableErrors);
      } else {
        setValidationErrors(undefined);
      }
      const allErrors = extractAllErrors(errors);
      setAllErrors(allErrors);
      logDebug(errors);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setValidationErrors(undefined);
    setAllErrors(undefined);
  };

  const handleSave = apiSubmitHandler(
    companyCreateFormContext.handleSubmit,
    companyCreateFormContext.setError,
    async (data, { setFormErrors }) => {
      try {
        setValidationErrors(undefined);
        setAllErrors(undefined);
        const createdCompany = await companyCreateMutation.create({
          data: {
            ...data,
            callCenterId: authUser.callCenterId || callCenter.id,
            accountId: account.id
          } as CompanyCreateApiDTO
        });
        enqueueSnackbar('Company created successfully');
        onClose({ action: 'CONFIRM', value: { createdCompany } });
      } catch (error) {
        if (error instanceof BadRequestError) {
          setValidationErrors(error.responseData?.nonFieldErrors || ['Failed to create company']);
          setFormErrors(error);
        }
      }
    },
    {
      onInvalid: (errors) => {
        setValidationErrors(undefined);
        enqueueSnackbar('Validation Error occurred. Please check inputs and try again', { variant: 'error' });
        const allErrors = extractAllErrors(errors);
        setAllErrors(allErrors);
        logDebug(errors);
      }
    }
  );

  const handleClose = () => {
    if (!companyCreateMutation.isPending) {
      onClose();
    }
  };

  const getStepContent = (step: number) => {
    const formValues = getValues();

    switch (step) {
      case 0: // Details
        return (
          <CompanyConfigDetailForm
            companyConfigDetailFormContext={
              companyCreateFormContext as unknown as UseFormReturn<CompanyConfigDetailFormValues>
            }
            account={account}
            nationalNumberGroups={appConfigQuery.data?.nationalNumberGroups}
          />
        );
      case 1: // Contacts
        return (
          <CompanyContactsForm
            companyContactsFormContext={companyCreateFormContext as unknown as UseFormReturn<CompanyContactsFormValues>}
          />
        );
      case 2: // Notes
        return (
          <CompanyConfigNotesForm
            companyConfigNotesFormContext={
              companyCreateFormContext as unknown as UseFormReturn<CompanyConfigNotesFormValues>
            }
          />
        );
      case 3: // Zip/Zones
        return (
          <CompanyConfigZipZonesForm
            companyConfigZipZonesFormContext={
              companyCreateFormContext as unknown as UseFormReturn<CompanyConfigZipZonesFormValues>
            }
            // Note - this is needed to pre-default the zone note values
            company={{ ...companyCreateFormContext.getValues(), isActive: true, dataProvider: null }}
          />
        );
      case 4: // Settings
        return (
          <CompanyConfigSettingsForm
            companyConfigSettingsFormContext={
              companyCreateFormContext as unknown as UseFormReturn<CompanyConfigSettingsFormValues>
            }
            callCenter={callCenter}
            voicesAreLoading={aiVoicesQuery.isFetching}
            aiVoices={aiVoicesQuery.data?.results.filter((voice) => voice.isActive) || []}
          />
        );
      case 5: // Billing
        return (
          <CompanyConfigBillingForm
            companyConfigBillingFormContext={
              companyCreateFormContext as unknown as UseFormReturn<CompanyConfigBillingFormValues>
            }
          />
        );
      case 6: // Review
        return (
          <Stack spacing={2} sx={{ padding: 2 }}>
            <Typography variant='h2' text='Review Company Details' />
            <Divider />
            <Stack spacing={1}>
              <Typography variant='h3' text='Basic Information' />
              <Typography text={`Name: ${formValues.name || 'Not set'}`} />
              <Typography
                text={`Address: ${formValues.addressLine1 || 'Not set'}, ${formValues.city || ''}, ${formValues.stateOrProvince || ''} ${formValues.zipOrPostal || ''}`}
              />
              <Typography component='span'>
                Trades: <TradesDisplay trades={formValues.callCenter?.trades || []} />
              </Typography>
              <Typography
                text={`Zips Serviced: ${
                  formValues.isZoned
                    ? formValues.zones?.map((zone) => zone.zipCodes.join(', ')).join(', ')
                    : formValues.callCenter?.zipCodesServiced?.join(', ') || 'N/A'
                }`}
                sx={{ overflowWrap: 'break-word', whiteSpace: 'pre-line' }}
              />
              {formValues?.callCenter?.dnisArray?.length ? (
                <Typography
                  text={`DNIS Numbers: ${formValues.callCenter?.dnisArray?.map((dnis) => formatPhone(dnis.phoneNumber)).join(', ') || 'N/A'}`}
                  sx={{ overflowWrap: 'break-word', whiteSpace: 'pre-line' }}
                />
              ) : (
                <WarningAlert>
                  <Typography>No DNIS numbers added</Typography>
                </WarningAlert>
              )}
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant='h3' text='Contact Information' />
              <Typography text={`Primary Contact: ${formValues.primaryContact?.name || 'Not set'}`} />
              <Typography text={`Secondary Contact: ${formValues.secondaryContact?.name || 'Not set'}`} />
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant='h3' text='Settings' />
              {callCenter.phoneCallWebhookEnabled && (
                <Typography
                  text={`Webhook Enabled: ${'phoneCallWebhookEnabled' in formValues.callCenter ? !!formValues.callCenter.phoneCallWebhookEnabled : 'false'}`}
                />
              )}
              <Typography
                text={`Reporting Enabled: ${'reporting' in formValues.callCenter ? !!formValues.callCenter.reporting?.shouldReport : 'false'}`}
              />
              <Typography
                text={`Company Category: ${
                  'reporting' in formValues.callCenter && formValues.callCenter.reporting
                    ? companyCategoryDisplayName(formValues.callCenter.reporting.companyCategory)
                    : 'N/A'
                }`}
              />
              <Typography
                text={`Invoiced Service: ${
                  'reporting' in formValues.callCenter && formValues.callCenter.reporting
                    ? InvoicedServiceEnum[formValues.callCenter.reporting.invoicedService]
                    : 'N/A'
                }`}
              />
            </Stack>
            <Divider />
            <Stack spacing={1}>
              <Typography variant='h3' text='Billing Information' />
              <Typography text={`Price Per Call: $${(formValues as Company).callCenter.billing.perCall}`} />
              <Typography
                text={`Price Per Appointment: $${(formValues as Company).callCenter.billing.perAppointment}`}
              />
              <Typography text={`Price Per Cancel: $${(formValues as Company).callCenter.billing.perCancel}`} />
              <Typography text={`Discount Percent: ${(formValues as Company).callCenter.billing.discount}%`} />
              <Typography
                text={`Weekly Integration Fee: $${(formValues as Company).callCenter.billing.weeklyIntegrationFee}`}
              />
              <Typography
                text={`Minimum Weekly Service Fee: $${(formValues as Company).callCenter.billing.minimumWeeklyServiceFee}`}
              />
            </Stack>
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <DialogWrapper
      dismissable={!companyCreateMutation.isPending}
      onClose={handleClose}
      titleText='Create Company'
      maxWidth='lg'
      {...dialogProps}>
      <BackdropSpinner open={companyCreateMutation.isPending} />
      <DialogContent sx={{ padding: 0, minWidth: '1000px' }}>
        <Stack spacing={3} sx={{ padding: 2 }}>
          <Stepper activeStep={activeStep} nonLinear>
            {steps.map(({ label }, index) => (
              <Step key={index} completed={completedSteps[index]}>
                <StepButton
                  onClick={() => handleGoToStep(index)}
                  disabled={companyCreateMutation.isPending || !completedSteps[index] || index === activeStep}>
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
          {validationErrors && validationErrors.length > 0 && (
            <ErrorDisplay errors={validationErrors} sx={{ borderRadius: 1 }} />
          )}
          {!!allErrors?.length && <ErrorDetailsAccordion errors={allErrors} />}
          <Stack sx={{ minHeight: '400px', overflow: 'auto', padding: 1 }}>{getStepContent(activeStep)}</Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button text='Cancel' onClick={handleClose} disabled={companyCreateMutation.isPending} />
        {activeStep > 0 && <Button text='Back' onClick={handleBack} disabled={companyCreateMutation.isPending} />}
        {activeStep < steps.length - 1 && (
          <Button text='Next' onClick={() => handleGoToStep(activeStep + 1)} variant='contained' />
        )}
        {activeStep === steps.length - 1 && (
          <Button
            text='Create Company'
            onClick={handleSave}
            variant='contained'
            color='primary'
            disabled={companyCreateMutation.isPending}
          />
        )}
      </DialogActions>
    </DialogWrapper>
  );
};

interface CompanyCreateDialog extends AsyncDialog<{ createdCompany: Company }, CompanyCreateDialogProps> {}
export const useCompanyCreateDialog = (): CompanyCreateDialog => useAsyncDialog(CompanyCreateDialog);
